import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { BusyIndicatorComponent, formatAbbreviatedNumber, TruncateTextComponent } from "@tylertech/common-components-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ForgeCardModule, ForgeIconButtonModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { ChartConfiguration, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "student-activities-monthly-revenue-chart",
    templateUrl: "./revenueChart.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        CommandBarComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        TruncateTextComponent,
    ]
})
export class RevenueChartComponent implements OnDestroy, OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $fileService = inject(FileService);
    private readonly $chartService = inject(ChartService);

    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;

    private _chartCriteria: IChartCriteria;
    readonly ChartTitle: string = "Student Activity Monthly Revenue";
    isLoaded: boolean;
    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.chartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartSubtitle(): string
    {
        let ret = "";
        const fiscal = this.chartCriteria?.Parameters?.find(x => x.Id === "SARevenueFiscal");
        const month = this.chartCriteria?.Parameters?.find(x => x.Id === "SARevenueMonth");
        if (fiscal?.Value)
        {
            const selectedFiscal = fiscal.Options.find(x => x.Value === fiscal.Value.toString());
            if (selectedFiscal)
            {
                ret += selectedFiscal.Display;
            }
        }
        if (month?.Value)
        {
            const monthName = month.Options.find(x => x.Value === month.Value.toString()).Display;
            ret += " &bull; " + monthName;
        }
        return ret;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = false;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        const chartColors = this.$chartService.getColors();

        this.chartConfig = {
            type: "bar",
            options: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<"bar">[]) =>
                            {
                                return tooltipItems[0].label;
                            },
                            label: (tooltipItem: TooltipItem<"bar">) =>
                            {
                                return formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "left",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
            },
            data: {
                labels: this.chartCriteria.ChartDatas[0].Labels,
                datasets: [
                    {
                        type: "bar",
                        label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                        data: this.chartCriteria.ChartDatas[0].Data,
                        backgroundColor: chartColors,
                        yAxisID: "y",
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
